import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { X, Plus, Trash2, Loader2, Clock, CalendarOff } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// 0 = Sunday, matching Date.getDay() and the backend's weekday column.
const WEEKDAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
  { value: 0, label: "Domingo" },
];

const inputClass =
  "px-2.5 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all";

const AppointmentAvailabilityModal = ({ open, onClose }) => {
  const isMounted = useRef(true);
  const [tab, setTab] = useState("hours");
  const [scope, setScope] = useState(""); // "" = business-wide
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [blackoutDraft, setBlackoutDraft] = useState({
    professionalId: "",
    startAt: "",
    endAt: "",
    reason: "",
  });

  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    if (!open) return;
    api
      .get("/users", { params: { pageNumber: 1 } })
      .then(({ data }) => isMounted.current && setUsers(data?.users || []))
      .catch(toastError);
  }, [open]);

  const loadRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointment-availabilities", {
        params: { professionalId: scope },
      });
      if (isMounted.current) setRows(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const loadBlackouts = async () => {
    try {
      const { data } = await api.get("/appointment-blackouts");
      if (isMounted.current) setBlackouts(data || []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (tab === "hours") loadRows();
    else loadBlackouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, scope]);

  const byWeekday = useMemo(() => {
    const map = new Map(WEEKDAYS.map((d) => [d.value, []]));
    rows.forEach((row) => {
      if (map.has(row.weekday)) map.get(row.weekday).push(row);
    });
    return map;
  }, [rows]);

  const handleAddRange = async (weekday) => {
    setSaving(true);
    try {
      await api.post("/appointment-availabilities", {
        professionalId: scope ? Number(scope) : null,
        weekday,
        startTime: "09:00",
        endTime: "17:00",
        slotMinutes: 30,
      });
      await loadRows();
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const handleUpdateRange = async (row, changes) => {
    try {
      await api.put(`/appointment-availabilities/${row.id}`, changes);
      await loadRows();
    } catch (err) {
      toastError(err);
      await loadRows();
    }
  };

  const handleDeleteRange = async (row) => {
    try {
      await api.delete(`/appointment-availabilities/${row.id}`);
      await loadRows();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCreateBlackout = async (e) => {
    e.preventDefault();
    if (!blackoutDraft.startAt || !blackoutDraft.endAt) return;
    setSaving(true);
    try {
      await api.post("/appointment-blackouts", {
        professionalId: blackoutDraft.professionalId
          ? Number(blackoutDraft.professionalId)
          : null,
        startAt: new Date(blackoutDraft.startAt).toISOString(),
        endAt: new Date(blackoutDraft.endAt).toISOString(),
        reason: blackoutDraft.reason || null,
      });
      setBlackoutDraft({ professionalId: "", startAt: "", endAt: "", reason: "" });
      await loadBlackouts();
      toast.success("Bloqueo creado");
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const handleDeleteBlackout = async (blackout) => {
    try {
      await api.delete(`/appointment-blackouts/${blackout.id}`);
      await loadBlackouts();
    } catch (err) {
      toastError(err);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl mx-4 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Clock className="w-4 h-4 text-indigo-600" />
            Disponibilidad
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-1.5 px-5 pt-4">
          {[
            { key: "hours", label: "Horarios semanales", icon: Clock },
            { key: "blackouts", label: "Bloqueos y feriados", icon: CalendarOff },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  tab === t.key
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "hours" ? (
          <>
            <div className="px-5 py-3">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                Horario de
              </p>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className={`${inputClass} w-full`}
              >
                <option value="">Horario general del negocio</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
              <p className="text-[11px] text-slate-400 mt-1.5">
                {scope
                  ? "Si este profesional no tiene franjas propias, se usa el horario general."
                  : "Se aplica a los profesionales que no tengan un horario propio."}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-2">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : (
                WEEKDAYS.map((day) => {
                  const ranges = byWeekday.get(day.value) || [];
                  return (
                    <div
                      key={day.value}
                      className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                          {day.label}
                        </p>
                        <button
                          onClick={() => handleAddRange(day.value)}
                          disabled={saving}
                          className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:underline disabled:opacity-50"
                        >
                          <Plus className="w-3 h-3" />
                          Añadir franja
                        </button>
                      </div>
                      {ranges.length === 0 ? (
                        <p className="text-[11px] text-slate-400">Cerrado</p>
                      ) : (
                        <div className="space-y-2">
                          {ranges.map((row) => (
                            <div key={row.id} className="flex items-center gap-2 flex-wrap">
                              <input
                                type="time"
                                defaultValue={row.startTime}
                                onBlur={(e) =>
                                  e.target.value !== row.startTime &&
                                  handleUpdateRange(row, { startTime: e.target.value })
                                }
                                className={inputClass}
                              />
                              <span className="text-xs text-slate-400">a</span>
                              <input
                                type="time"
                                defaultValue={row.endTime}
                                onBlur={(e) =>
                                  e.target.value !== row.endTime &&
                                  handleUpdateRange(row, { endTime: e.target.value })
                                }
                                className={inputClass}
                              />
                              <span className="text-xs text-slate-400">cada</span>
                              <select
                                value={row.slotMinutes}
                                onChange={(e) =>
                                  handleUpdateRange(row, {
                                    slotMinutes: Number(e.target.value),
                                  })
                                }
                                className={inputClass}
                              >
                                {[10, 15, 20, 30, 45, 60, 90, 120].map((m) => (
                                  <option key={m} value={m}>{m} min</option>
                                ))}
                              </select>
                              <button
                                onClick={() => handleDeleteRange(row)}
                                className="ml-auto p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <>
            <form
              onSubmit={handleCreateBlackout}
              className="px-5 py-4 mt-3 border-y border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 space-y-3"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                    Desde
                  </p>
                  <input
                    type="datetime-local"
                    required
                    value={blackoutDraft.startAt}
                    onChange={(e) =>
                      setBlackoutDraft({ ...blackoutDraft, startAt: e.target.value })
                    }
                    className={`${inputClass} w-full`}
                  />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                    Hasta
                  </p>
                  <input
                    type="datetime-local"
                    required
                    value={blackoutDraft.endAt}
                    onChange={(e) =>
                      setBlackoutDraft({ ...blackoutDraft, endAt: e.target.value })
                    }
                    className={`${inputClass} w-full`}
                  />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                    Afecta a
                  </p>
                  <select
                    value={blackoutDraft.professionalId}
                    onChange={(e) =>
                      setBlackoutDraft({ ...blackoutDraft, professionalId: e.target.value })
                    }
                    className={`${inputClass} w-full`}
                  >
                    <option value="">Todos</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                    Motivo (opcional)
                  </p>
                  <input
                    type="text"
                    placeholder="Feriado, vacaciones..."
                    value={blackoutDraft.reason}
                    onChange={(e) =>
                      setBlackoutDraft({ ...blackoutDraft, reason: e.target.value })
                    }
                    className={`${inputClass} w-full`}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Bloquear
              </button>
            </form>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
              {blackouts.length === 0 ? (
                <p className="text-center py-8 text-sm text-slate-400">
                  No hay bloqueos configurados.
                </p>
              ) : (
                blackouts.map((blackout) => (
                  <div
                    key={blackout.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                  >
                    <CalendarOff className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-900 dark:text-slate-100">
                        {new Date(blackout.startAt).toLocaleString()} → {new Date(blackout.endAt).toLocaleString()}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {blackout.professional?.name || "Todos los profesionales"}
                        {blackout.reason ? ` · ${blackout.reason}` : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteBlackout(blackout)}
                      className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AppointmentAvailabilityModal;
